import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROASTS } from "@/lib/brew";
import { usePrefs } from "@/context/PrefsContext";
import { toast } from "sonner";

const SEGMENTS = [
  { key: "temp_unit", label: "Temperature", options: [["C", "Celsius"], ["F", "Fahrenheit"]] },
  { key: "weight_unit", label: "Weight", options: [["g", "Grams"], ["oz", "Ounces"]] },
  { key: "volume_unit", label: "Volume", options: [["ml", "Millilitres"], ["floz", "Fluid ounces"]] },
  { key: "default_mode", label: "Default mode", options: [["guided", "Guided"], ["custom", "Custom"]] },
];

const TOGGLES = [
  ["sound", "Sound cues"],
  ["vibration", "Vibration cues"],
  ["keep_awake", "Keep screen awake"],
  ["reduced_motion", "Reduced motion"],
];

export default function Preferences() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const { reloadPrefs } = usePrefs();

  useEffect(() => {
    api.get("/preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);

  const save = async (next) => {
    setPrefs(next);
    try {
      const { customer_id, ...body } = next;
      await api.put("/preferences", body);
      if (reloadPrefs) reloadPrefs();
    } catch { toast.error("Could not save preference"); }
  };

  if (!prefs) return <div><PageHeader title="Brewing Preferences" onBack={() => navigate("/account")} /></div>;

  return (
    <div data-testid="preferences-page">
      <PageHeader title="Brewing Preferences" onBack={() => navigate("/account")} />
      <div className="px-5 pt-5 space-y-5 pb-8">
        <p className="text-xs text-[#EDE0CC]/60">Display units only — changing units never alters your saved recipe values.</p>

        {SEGMENTS.map((seg) => (
          <div key={seg.key}>
            <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/60 mb-2">{seg.label}</div>
            <div className="flex bg-[#051540] rounded-full p-1">
              {seg.options.map(([val, label]) => (
                <button key={val} onClick={() => save({ ...prefs, [seg.key]: val })} data-testid={`pref-${seg.key}-${val}`}
                  className={`flex-1 h-10 rounded-full text-sm font-medium transition-colors ${prefs[seg.key] === val ? "bg-[#EDE0CC] text-[#081F5C]" : "text-[#EDE0CC]/70"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div>
          <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/60 mb-2">Default roast profile</div>
          <Select value={prefs.default_roast} onValueChange={(v) => save({ ...prefs, default_roast: v })}>
            <SelectTrigger data-testid="pref-default-roast" className="h-12 rounded-xl bg-[#051540] border-[#EDE0CC]/30 text-[#EDE0CC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0D2875] border-[#EDE0CC]/20 text-[#EDE0CC]">
              {ROASTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          {TOGGLES.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between rounded-2xl border border-[#EDE0CC]/15 bg-[#0D2875] p-4">
              <span className="text-[#EDE0CC]">{label}</span>
              <Switch checked={!!prefs[key]} onCheckedChange={(v) => save({ ...prefs, [key]: v })} data-testid={`pref-${key}`} className="data-[state=checked]:bg-[#EDE0CC]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
